/* ============================================================
   Busca de lugares — duas fontes, as duas de graça

   Photon (komoot) e Nominatim, ambas em cima do OpenStreetMap, nenhuma
   pede chave de API. São consultadas em paralelo e os resultados entram
   numa lista só, sem repetir:

   - Photon acerta mais em nome de estabelecimento e em busca parcial;
   - Nominatim acerta mais em endereço e em nome completo.

   As duas aceitam um ponto de referência, e a gente manda o centro do
   mapa. Sem isso, buscar "Bar do Zé" traz um bar do Zé em qualquer canto
   do mundo.

   O OSM tem bem menos estabelecimento cadastrado que o Google Maps. Por
   isso a tela sempre deixa digitar o nome na mão e marcar o ponto no
   mapa — a busca é atalho, não requisito.
   ============================================================ */

const PHOTON = "https://photon.komoot.io/api/";
const NOMINATIM = "https://nominatim.openstreetmap.org/search";

export interface Coords { lat: number; lng: number }

export interface Lugar {
  name: string;
  detail: string;
  kind: string;
  coords: Coords;
}

const TIPO: Record<string, string> = {
  restaurant: "Restaurante", fast_food: "Lanchonete", cafe: "Café", bar: "Bar",
  pub: "Bar", bakery: "Padaria", ice_cream: "Sorveteria", pizzeria: "Pizzaria",
  food_court: "Praça de alimentação", marketplace: "Mercado", supermarket: "Mercado",
  confectionery: "Doceria", deli: "Empório", nightclub: "Balada",
};

const arredonda = (n: number) => Number(Number(n).toFixed(6));

async function doPhoton(termo: string, perto: Coords | null): Promise<Lugar[]> {
  // O Photon só aceita lang default/de/en/fr — mandar "pt" faz ele
  // devolver erro em vez de resultado.
  const params = new URLSearchParams({ q: termo, limit: "10" });
  if (perto) { params.set("lat", String(perto.lat)); params.set("lon", String(perto.lng)); }

  const res = await fetch(`${PHOTON}?${params}`);
  if (!res.ok) throw new Error("photon");
  const data = await res.json() as { features?: Array<Record<string, never>> };

  return (data.features ?? []).map((f) => {
    const feature = f as unknown as {
      properties?: Record<string, string>;
      geometry?: { coordinates?: number[] };
    };
    const p = feature.properties ?? {};
    const [lng, lat] = feature.geometry?.coordinates ?? [];
    const detail = [
      p["osm_value"] ? TIPO[p["osm_value"]] : undefined,
      [p["street"], p["housenumber"]].filter(Boolean).join(", "),
      p["district"] ?? p["city"],
      p["state"],
    ].filter(Boolean).join(" · ");

    return {
      name: p["name"] ?? p["street"] ?? "",
      detail,
      kind: p["osm_value"] ?? "",
      coords: { lat: arredonda(lat ?? NaN), lng: arredonda(lng ?? NaN) },
    };
  }).filter((r) => r.name && Number.isFinite(r.coords.lat));
}

async function doNominatim(termo: string, perto: Coords | null): Promise<Lugar[]> {
  const params = new URLSearchParams({
    format: "jsonv2", limit: "8", addressdetails: "1",
    "accept-language": "pt-BR", q: termo,
  });
  if (perto) {
    // caixa de ~1,2° em volta do ponto de referência, sem excluir o resto
    const d = 0.6;
    params.set("viewbox", [perto.lng - d, perto.lat + d, perto.lng + d, perto.lat - d].join(","));
  }

  const res = await fetch(`${NOMINATIM}?${params}`, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error("nominatim");
  const data = await res.json() as Array<Record<string, string>>;

  return data.map((r) => ({
    name: r["name"] || String(r["display_name"] ?? "").split(",")[0] || "",
    detail: String(r["display_name"] ?? "").split(",").slice(1, 4).join(",").trim(),
    kind: r["type"] ?? "",
    coords: { lat: arredonda(Number(r["lat"])), lng: arredonda(Number(r["lon"])) },
  })).filter((r) => r.name);
}

/** O caminho inverso: da coordenada para o nome do lugar. */
export async function descreverPonto({ lat, lng }: Coords): Promise<{ name: string; detail: string }> {
  try {
    const params = new URLSearchParams({
      format: "jsonv2", lat: String(lat), lon: String(lng),
      zoom: "18", "accept-language": "pt-BR",
    });
    const res = await fetch(`${NOMINATIM.replace("/search", "/reverse")}?${params}`,
      { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error("reverse");

    const r = await res.json() as { name?: string; address?: Record<string, string> };
    const a = r.address ?? {};
    const nome = r.name
      || [a["road"], a["house_number"]].filter(Boolean).join(", ")
      || a["suburb"] || a["neighbourhood"] || a["city_district"]
      || a["city"] || a["town"] || a["village"];
    const detalhe = [
      a["suburb"] ?? a["neighbourhood"],
      a["city"] ?? a["town"] ?? a["village"],
      a["state"],
    ].filter(Boolean).join(" · ");

    return { name: nome ?? "", detail: detalhe };
  } catch {
    return { name: "", detail: "" };
  }
}

const distancia = (a: Coords | null, b: Coords | null) =>
  (!a || !b ? Infinity : Math.hypot(a.lat - b.lat, a.lng - b.lng));

// Lugar de comer aparece antes de rua, bairro e cidade — é um app de comida.
const COMIDA = new Set(Object.keys(TIPO));
const peso = (r: Lugar, perto: Coords | null) =>
  (COMIDA.has(r.kind) ? 0 : 1) * 100 + distancia(r.coords, perto);

export async function buscarLugares(termo: string, perto: Coords | null): Promise<Lugar[]> {
  const [photon, nominatim] = await Promise.allSettled([
    doPhoton(termo, perto),
    doNominatim(termo, perto),
  ]);
  if (photon.status === "rejected" && nominatim.status === "rejected") {
    throw new Error("busca indisponível");
  }

  const juntos: Lugar[] = [];
  const vistos = new Set<string>();
  const todos = [
    ...(photon.status === "fulfilled" ? photon.value : []),
    ...(nominatim.status === "fulfilled" ? nominatim.value : []),
  ];

  for (const r of todos) {
    // mesmo lugar vindo das duas fontes: coordenada arredondada como chave
    const chave = `${r.name.toLowerCase()}|${r.coords.lat.toFixed(3)},${r.coords.lng.toFixed(3)}`;
    if (vistos.has(chave)) continue;
    vistos.add(chave);
    juntos.push(r);
  }

  juntos.sort((a, b) => peso(a, perto) - peso(b, perto));
  return juntos.slice(0, 12);
}
